import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePendingApplicationsCount } from '@/hooks/usePendingApplicationsCount';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { PermissionsProvider } from '@/lib/permissions';
import AdminMenu from './AdminMenu';

interface AdminLayoutProps {
  children: ReactNode;
}

const AdminLayoutContent = ({ children }: AdminLayoutProps) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem('admin-desktop-menu-open');
    if (stored !== null) {
      setIsDesktopMenuOpen(stored === 'true');
    }
  }, []);

  const toggleDesktopMenu = () => {
    setIsDesktopMenuOpen((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('admin-desktop-menu-open', String(next));
      }
      return next;
    });
  };

  const { data: pendingApplicationsCount = 0 } = usePendingApplicationsCount();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      <a
        href="#admin-main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:shadow-md"
      >
        Skip to main content
      </a>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden shrink-0 border-r bg-card transition-[width] duration-200 lg:block ${
          isDesktopMenuOpen ? 'w-64' : 'w-16'
        }`}
      >
        <div className="flex h-16 items-center border-b px-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <img
              src="/promoter-os-mark.png"
              alt="Promoter OS"
              className="h-8 w-8 shrink-0 rounded-lg object-cover"
            />
            {isDesktopMenuOpen && (
              <h1 className="truncate text-lg font-bold text-primary">Promoter OS</h1>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDesktopMenu}
            aria-label={isDesktopMenuOpen ? 'Collapse navigation menu' : 'Expand navigation menu'}
            title={isDesktopMenuOpen ? 'Collapse menu' : 'Expand menu'}
            className="h-8 w-8 shrink-0"
          >
            {isDesktopMenuOpen ? (
              <PanelLeftClose className="h-4 w-4" />
            ) : (
              <PanelLeftOpen className="h-4 w-4" />
            )}
          </Button>
        </div>
        {isDesktopMenuOpen ? (
          <nav className="flex h-[calc(100vh-4rem)] flex-col p-4">
            <AdminMenu
              pendingApplicationsCount={pendingApplicationsCount}
              onSignOut={handleSignOut}
            />
          </nav>
        ) : (
          <div className="flex h-[calc(100vh-4rem)] items-start justify-center pt-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDesktopMenu}
              aria-label="Open navigation menu"
              title="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        )}
      </aside>

      {/* Mobile Header */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b bg-card px-4 lg:hidden">
          <div className="flex items-center gap-2">
            <img
              src="/promoter-os-mark.png"
              alt="Promoter OS"
              className="h-8 w-8 rounded-lg object-cover"
            />
            <h1 className="text-lg font-bold text-primary">Promoter OS</h1>
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open navigation menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <div className="flex h-16 items-center border-b px-6">
                <div className="flex items-center gap-2">
                  <img
                    src="/promoter-os-mark.png"
                    alt="Promoter OS"
                    className="h-8 w-8 rounded-lg object-cover"
                  />
                  <h1 className="text-xl font-bold text-primary">Promoter OS</h1>
                </div>
              </div>
              <nav className="flex h-[calc(100vh-4rem)] flex-col p-4">
                <AdminMenu
                  pendingApplicationsCount={pendingApplicationsCount}
                  onSignOut={handleSignOut}
                />
              </nav>
            </SheetContent>
          </Sheet>
        </header>

        {/* Main Content */}
        <main
          id="admin-main-content"
          className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-5 xl:p-6"
        >
          <div className="min-w-0 max-w-full">{children}</div>
        </main>
      </div>
    </div>
  );
};

const AdminLayout = ({ children }: AdminLayoutProps) => (
  <PermissionsProvider>
    <AdminLayoutContent>{children}</AdminLayoutContent>
  </PermissionsProvider>
);

export default AdminLayout;
